import { getApiErrorMessage } from '../../utils/apiError';

/**
 * Single home for turning a PZ API failure into a Polish sentence an operator
 * can act on.
 *
 * The backend has no i18n; it emits stable `code` values (see
 * `backend/src/services/warehouseDocumentStatusPolicy.js` and the PZ routes) and
 * this module is where each one becomes operator wording. Previously this map
 * lived inside `pages/PZDetail.tsx` while `pages/PZDocuments.tsx` carried its
 * own partial copy, so the same backend rejection read as a precise sentence on
 * one screen and a generic "Nie udało się zapisać" on the other.
 *
 * When the mapping produces nothing, we fall back to `getApiErrorMessage`, which
 * uses the generic Polish fallback key rather than backend validation prose.
 * The fallback key itself must name the operation that failed — never a bare
 * "Błąd". See `utils/feedback.ts` for the full copy policy.
 */
const PZ_ERROR_CODE_KEYS: Record<string, string> = {
  PZ_NUMBER_EXISTS: 'pz:error.pzNumberExists',
  PZ_DELETE_BLOCKED_STATUS: 'pz:error.deleteBlockedReceived',
  PZ_UNIT_PRICE_REQUIRED: 'pz:validation.priceRequiredBeforeStatus',
  PZ_CORRECTION_REASON_REQUIRED: 'pz:error.correctionReasonRequired',
  PZ_CORRECTION_STALE: 'pz:error.correctionStale',
  PZ_CORRECTION_ITEMS_REQUIRED: 'pz:error.correctionItemsRequired',
  PZ_CORRECTION_INSUFFICIENT_STOCK: 'pz:error.correctionInsufficientStock',
  PZ_CORRECTION_ISSUED_IDENTITY: 'pz:error.correctionIssuedIdentity',
  PZ_CORRECTION_ADJUSTED_BATCH: 'pz:error.correctionAdjustedBatch',
  PZ_UPDATE_BLOCKED_RECEIVED: 'pz:error.updateBlockedReceived',
  PZ_UPDATE_BLOCKED_CANCELLED: 'pz:error.updateBlockedCancelled',
  PZ_INVALID_STATUS_TRANSITION: 'pz:error.invalidStatusTransition',
  PZ_SUPPLIER_ORDER_TRACE_LOCKED: 'pz:error.supplierOrderTraceLocked',
  PZ_TRANSPORT_QUANTITY_UNRESOLVED: 'pz:error.transportQuantityUnresolved',
  PZ_QUANTITY_RECEIVED_EXCEEDS_ORDERED: 'pz:validation.quantityReceivedRange',
  INCOMPATIBLE_INVENTORY_UNIT: 'pz:error.incompatibleInventoryUnit',
  // Etap 2: the chosen transport is not allocated to this supplier order —
  // reachable when the transport's supplier-order links changed in another tab
  // between opening the picker and confirming.
  PZ_TRANSPORT_NOT_ALLOCATED_TO_SUPPLIER_ORDER: 'pz:deliveryTransports.notAllocatedError',
  PZ_REQUIRES_PURCHASE_INVOICE: 'pz:error.receiptRequiresPurchaseInvoice',
};

/**
 * Backend codes that describe a blocked operation rather than a transient
 * failure: the operator has to change something (or reload) before retrying, so
 * the toast should stay on screen until dismissed.
 */
const PZ_BLOCKING_ERROR_CODES = new Set([
  'PZ_UPDATE_BLOCKED_RECEIVED',
  'PZ_UPDATE_BLOCKED_CANCELLED',
  'PZ_DELETE_BLOCKED_STATUS',
  'PZ_INVALID_STATUS_TRANSITION',
  'PZ_SUPPLIER_ORDER_TRACE_LOCKED',
  'PZ_TRANSPORT_QUANTITY_UNRESOLVED',
  'PZ_TRANSPORT_NOT_ALLOCATED_TO_SUPPLIER_ORDER',
  'INCOMPATIBLE_INVENTORY_UNIT',
  'PZ_REQUIRES_PURCHASE_INVOICE',
]);

export function isBlockingPZError(error: any): boolean {
  const code = error?.response?.data?.code;
  return typeof code === 'string' && PZ_BLOCKING_ERROR_CODES.has(code);
}

/**
 * @param fallbackKey i18n key used when the server says nothing specific. Must
 *   name the failed operation, e.g. `pz:error.saveFailed`.
 * @param fallbackParams interpolation for the fallback key — pass the document
 *   number so even the generic path identifies the object.
 */
export function getPZErrorMessage(
  error: any,
  t: any,
  fallbackKey: string,
  fallbackParams?: Record<string, unknown>
): string {
  const code = error?.response?.data?.code;
  if (typeof code === 'string' && PZ_ERROR_CODE_KEYS[code]) {
    return t(PZ_ERROR_CODE_KEYS[code]);
  }
  return getApiErrorMessage(error, t, fallbackKey, undefined, fallbackParams);
}

/**
 * Receipt failures after a successful document save need different wording:
 * the PZ exists, but stock movement did not happen. The exclusive invoice
 * workflow has no command that attaches an invoice to an existing manual PZ,
 * so the message must not tell OPERATIONS to create another PZ.
 */
export function getPZReceiptErrorMessage(
  error: any,
  t: any,
  fallbackKey: string,
  documentSaved = false,
): string {
  const code = error?.response?.data?.code;
  if (documentSaved && code === 'PZ_REQUIRES_PURCHASE_INVOICE') {
    return t('pz:error.savedButNotReceivedRequiresPurchaseInvoice');
  }
  return getPZErrorMessage(error, t, fallbackKey);
}
